import { memo, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Menu, MenuItem, Popover, Save, Text } from '@/components/core';
import { AddIcon } from '@/components/icons';
import { useGetAgentFromPath } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { findModelId } from '@/utils';

import { useGetSamplePayloads } from './useGetSamplePayloads';

export type Variable = {
  id: string;
  name?: string | null;
};

export type VariablesMenuProps = {
  onSelect: (variable: string) => void;
};

export const IVariablesMenu = ({ onSelect }: VariablesMenuProps) => {
  const { data: agent } = useGetAgentFromPath();
  const { data: model, isPending: isPendingModel } = useGetModel(toModelId(findModelId(agent)));
  const { payloads, isPending: isPendingPayloads } = useGetSamplePayloads(agent?.agentId);
  const isPending = isPendingModel || isPendingPayloads;

  const variables = useMemo(() => {
    const variablesMap: Record<string, Variable> = {};

    for (const payload of payloads) {
      if (typeof payload === 'object' && payload !== null) {
        for (const [id, value] of Object.entries(payload)) {
          const name =
            typeof value === 'object' && value !== null && typeof value?.name === 'string' ? value.name : null;
          variablesMap[id] = { id, name };
        }
      }
    }

    for (const [id, fieldConfig] of Object.entries(model?.fieldConfig ?? {})) {
      const name = typeof fieldConfig?.name === 'string' ? fieldConfig.name : null;
      variablesMap[id] = { id, name };
    }

    return Array.from(Object.values(variablesMap)).toSorted((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
  }, [model?.fieldConfig, payloads]);

  return (
    <MenuTrigger>
      <Button className="!p-0.5" variant="plain">
        <AddIcon />
      </Button>

      <Popover>
        {isPending ? (
          <Save message="Loading variables" />
        ) : (
          <Menu>
            {variables.map((variable) => (
              <MenuItem key={variable.id} onAction={() => onSelect(variable.id)}>
                <div className="flex items-center rounded-md border border-gray-200 px-1.5 py-0.5">
                  <Text size="xs">{variable.name ?? variable.id}</Text>
                </div>
                {variable.name ? (
                  <Text size="xs" className="!text-gray-500">
                    # {variable.id}
                  </Text>
                ) : null}
              </MenuItem>
            ))}
          </Menu>
        )}
      </Popover>
    </MenuTrigger>
  );
};

export const VariablesMenu = memo(IVariablesMenu) as typeof IVariablesMenu;
